<script setup>
import { defineEmits } from "vue";
import { useBasketStore } from "@/stores/basket";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { onMounted, ref, computed } from "vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import AppNotice from "@/components/AppNotice.vue";

const emit = defineEmits(["close"]);
const basket = useBasketStore();
const router = useRouter();
const userStore = useUserStore();

const isLoading = ref(false);
const mobileMenuOpen = ref(false);

onMounted(() => {
  if (!userStore.loggedIn) {
    userStore.getUser();
  }
});

router.afterEach(() => {
  const element = document.querySelector(".main-modal-content");
  if (element) {
    element.scrollTop = 0;
  }
});

const closeModal = () => {
  emit("close");
};

const openCloseMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const userName = computed(() => {
  return userStore.user
    ? "(" +
        userStore.user.id +
        ")" +
        userStore.user?.first_name +
        " " +
        userStore.user?.last_name
    : false;
});

const logout = async () => {
  isLoading.value = true;
  await userStore.logout();
  isLoading.value = false;
  router.push("/");
};
</script>

<template>
  <div class="main-modal">
    <div class="main-modal-inner">
      <div class="close-modal" @click="closeModal">
        <i class="fontawesome-icon fa-times fas"></i>
      </div>
      <div class="header-with-title">
        <slot></slot>
        <div class="burger" @click="openCloseMobileMenu">
          <svg
            width="30px"
            height="30px"
            viewBox="0 0 12 12"
            xml:space="preserve"
          >
            <g>
              <rect fill="#fff" height="1" width="11" x="0.5" y="5.5" />
              <rect fill="#fff" height="1" width="11" x="0.5" y="2.5" />
              <rect fill="#fff" height="1" width="11" x="0.5" y="8.5" />
            </g>
          </svg>
        </div>
      </div>
      <nav class="menu-holder">
        <ul :class="{ open: mobileMenuOpen }" @click="openCloseMobileMenu">
          <li>
            <RouterLink to="/">
              <i class="glyphicon fa-calendar-alt far" aria-hidden="true"></i>
              {{ $t('menu.terms') }}
            </RouterLink>
          </li>

          <li v-if="!userStore.loggedIn">
            <RouterLink to="/register">
              <i class="glyphicon fa-user far" aria-hidden="true"></i>
              {{ $t('menu.register') }}
            </RouterLink>
          </li>
          <li v-if="userStore.loggedIn">
            <RouterLink to="/my-profile">
              <i class="glyphicon fa-user far" aria-hidden="true"></i>
              {{ $t('menu.my_account') }}
            </RouterLink>
          </li>
          <li>
            <RouterLink to="/reservation">
              <i
                class="glyphicon fa-shopping-basket far"
                aria-hidden="true"
              ></i>
              {{ $t('menu.reservation') }}
              <span v-if="basket.basket.length" class="reserve-circle">
                {{ basket.basket.length }}
              </span>
            </RouterLink>
          </li>
          <li v-if="!userStore.loggedIn" class="login-logout">
            <RouterLink to="/login">
              <i class="glyphicon fa-user far" aria-hidden="true"></i>
              {{ $t('auth.login') }}
            </RouterLink>
          </li>
          <li class="login-logout" v-if="userStore.loggedIn">
            <span class="user-info">
              <i class="glyphicon fa-user far" aria-hidden="true"></i>
              {{ userName }}
            </span>
            <a href="" @click.prevent="logout">
              <i class="glyphicon fa-door-open far" aria-hidden="true"></i>
              {{ $t('auth.logout') }}
            </a>
          </li>
        </ul>
      </nav>
      <div class="main-modal-content">
        <app-notice></app-notice>
        <RouterView></RouterView>
        <LoadingSpinner v-if="isLoading" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
a:hover,
a.router-link-active {
  color: #eac33d !important;
}

.menu-holder {
  position: relative;
}
.burger {
  display: none;

  @media screen and (max-width: 1000px) {
    display: block;
    font-size: 30px;
    cursor: pointer;
  }
}

a {
  text-decoration: none !important;
  color: white !important;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 16px;
}
.main-modal {
  z-index: 9999999999;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 20px;
  text-align: left;

  @media screen and (max-width: 800px) {
    padding: 35px 20px;
  }
}

.header-with-title {
  padding: 25px 15px;
  background-color: var(--awb-color3);
  color: white;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-with-title > * {
  color: white;
}

.main-modal-content {
  padding: 35px;
  overflow-y: auto;
  height: 100%;

  @media screen and (max-width: 800px) {
    padding: 20px;
  }
}

ul {
  list-style: none;
  margin: 0px 0px 0px 0px;
  display: flex;
  justify-content: flex-start;
  background-color: var(--awb-color3);
  padding: 15px;
  border-top: 1px solid #676767;

  .login-logout {
    color: white;
    text-decoration: none;

    .user-info {
      margin-right: 15px;
      text-decoration: none;
      text-transform: uppercase;
    }
  }

  @media screen and (max-width: 1000px) {
    display: none;

    &.open {
      display: block;
      position: absolute;
      top: 0px;
      left: 0px;
      right: 0px;
      height: auto;
      z-index: 99;
    }
  }

  li {
    margin-right: 10px;
    margin-left: 10px;
    text-decoration: underline;

    &.login-logout {
      margin-left: auto;
    }

    &:first-of-type {
      margin-left: 0px;
    }
  }

  @media screen and (max-width: 1000px) {
    flex-direction: column;
    li {
      margin: 0px;
      padding: 5px 0px;

      &.login-logout {
        margin-left: 0px;
      }
    }
  }
}

.main-modal-inner {
  background-color: white;
  border-radius: 10px;
  position: relative;
  width: 100%;
  height: 100%;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.close-modal {
  position: absolute;
  top: -25px;
  right: -25px;
  z-index: 99;
  i {
    cursor: pointer;
    color: white;
    background-color: grey;
    padding: 10px;
    height: 50px;
    width: 50px;
    text-align: center;
    font-size: 30px;
    border-radius: 100%;
    &:hover {
      background-color: var(--awb-color4);
      color: var(--awb-color3);
    }
  }
}

.reserve-circle {
  display: inline-flex;
  width: 20px;
  height: 20px;
  background-color: #eac33d;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  font-size: 12px;
  color: white;
}
</style>
